import React, { useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { updateProfile, testEmail } from '../utils/api';
import { FiSave, FiSend, FiUser, FiMail, FiShield } from 'react-icons/fi';
import toast from 'react-hot-toast';

const SettingsPage = () => {
  const { user, setUser } = useAuth();
  const [name, setName] = useState(user?.name || '');
  const [notificationEmail, setNotificationEmail] = useState(user?.notificationEmail || user?.email || '');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await updateProfile({ name, notificationEmail });
      setUser(res.data.user);
      toast.success('Profile updated');
    } catch { toast.error('Failed to update'); }
    setSaving(false);
  };

  const handleTestEmail = async () => {
    setTesting(true);
    try {
      const res = await testEmail();
      if (res.data.success) toast.success('Test email sent! Check your inbox.');
      else toast.error('Failed to send test email');
    } catch { toast.error('Email test failed'); }
    setTesting(false);
  };

  return (
    <div className="animate-in" style={{ maxWidth: 600 }}>
      <h1 style={{ fontSize: 26, fontWeight: 800, letterSpacing: '-0.5px', marginBottom: 8 }}>Settings</h1>
      <p style={{ color: 'var(--text-muted)', fontSize: 14, marginBottom: 28 }}>Manage your account and notification preferences</p>

      <form onSubmit={handleSave}>
        <div className="card" style={{ padding: 28, marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 700, marginBottom: 20, color: 'var(--accent)' }}>
            <FiUser size={18} /> Profile
          </div>

          <div className="form-group">
            <label>Name</label>
            <input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" />
          </div>

          <div className="form-group">
            <label>Login Email</label>
            <input value={user?.email || ''} disabled style={{ opacity: 0.5 }} />
            <div className="form-hint">Login email cannot be changed</div>
          </div>

          <div className="form-group">
            <label>Notification Email</label>
            <input value={notificationEmail} onChange={e => setNotificationEmail(e.target.value)}
              placeholder="Where to send alerts" type="email" />
            <div className="form-hint">Alerts will be sent to this email address</div>
          </div>

          <button type="submit" className="btn btn-primary" disabled={saving}>
            <FiSave size={14} /> {saving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>

      {/* Email Test */}
      <div className="card" style={{ padding: 28, marginBottom: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 700, marginBottom: 12, color: 'var(--accent)' }}>
          <FiMail size={18} /> Email Configuration
        </div>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginBottom: 16 }}>
          Send a test email to verify your notification setup is working correctly.
        </p>
        <button type="button" className="btn btn-secondary" onClick={handleTestEmail} disabled={testing}>
          <FiSend size={14} /> {testing ? 'Sending...' : 'Send Test Email'}
        </button>
      </div>

      {/* Setup Guide */}
      <div className="card" style={{ padding: 28 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 16, fontWeight: 700, marginBottom: 12, color: 'var(--accent)' }}>
          <FiShield size={18} /> How monitoring works
        </div>
        <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.8 }}>
          <p style={{ marginBottom: 12 }}>Create a monitor with a website URL and a condition in plain language.</p>
          <ol style={{ paddingLeft: 20 }}>
            <li>Preview the page to confirm the data is readable.</li>
            <li>Describe exactly which item, unit, and threshold to check.</li>
            <li>Choose a schedule and notification method.</li>
            <li>Run the monitor once to confirm the extracted value and answer.</li>
          </ol>
          <p style={{ marginTop: 12 }}>
            <strong>Example:</strong> “Return true if the 22KT gold price is less than 320000 LKR.” If the value cannot be verified, the check shows an error instead of a false answer.
          </p>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
